import React from "react";
import { Box, Card, CardContent, Chip, Grid, Typography } from "@mui/material";

function DetailItem({ title, value, color }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        color="#6D6D6D"
        variant="body2"
        component="div"
        sx={{ mb: 0.5 }}
      >
        {title}
      </Typography>
      <Typography
        variant="subtitle1"
        component="div"
        sx={{
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          color: color || "inherit",
          fontWeight: color ? 600 : 400,
        }}
      >
        {value || "N/A"}
      </Typography>
    </Box>
  );
}

function CNVOverviewTable({ cnv }) {
  if (!cnv) {
    return (
      <Box
        sx={{
          justifyContent: "center",
          display: "flex",
          width: "100%",
          p: 2,
        }}
      >
        <Typography variant="body2">
          Please Select a CNV to View Details
        </Typography>
      </Box>
    );
  }

  // Helper to format size
  const formatSize = (size) => {
    if (!size) return "N/A";
    if (size >= 1000000) {
      return `${(size / 1000000).toFixed(2)} Mb (${size.toLocaleString()} bp)`;
    } else if (size >= 1000) {
      return `${(size / 1000).toFixed(2)} kb (${size.toLocaleString()} bp)`;
    }
    return `${size.toLocaleString()} bp`;
  };

  // Get color based on CNV type
  const getCNVTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "deletion":
        return "#d32f2f";
      case "duplication":
        return "#1976d2";
      case "amplification":
        return "#f57c00";
      default:
        return "#757575";
    }
  };

  // Get color based on clinical significance
  const getSignificanceColor = (significance) => {
    if (!significance) return "#747b87";
    const lower = significance.toLowerCase();
    if (lower.includes("pathogenic") && !lower.includes("likely")) {
      return "#ab0e0e";
    } else if (lower.includes("likely pathogenic")) {
      return "#eba721";
    } else if (lower.includes("benign")) {
      return "#1AB29C";
    } else if (lower.includes("uncertain")) {
      return "#747b87";
    }
    return "#747b87";
  };

  return (
    <Grid
      container
      spacing={2}
      sx={{
        background: "linear-gradient(45deg, #F2F2F2, #e7e7e7)",
        borderRadius: 3,
        p: 2,
      }}
    >
      {/* Basic CNV Information Card */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: "100%", boxShadow: 2 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                pb: 1,
                borderBottom: "2px solid #2196f3",
                color: "#171A1E",
              }}
            >
              CNV Information
            </Typography>
            <DetailItem title="Gene Symbol" value={cnv.gene_symbol} />
            <DetailItem
              title="CNV Type"
              value={cnv.cnv_type}
              color={getCNVTypeColor(cnv.cnv_type)}
            />
            <DetailItem
              title="Genomic Location"
              value={`${cnv.chromosome}:${cnv.start_position}-${cnv.end_position}`}
            />
            <DetailItem title="Size" value={formatSize(cnv.size)} />
            <Box sx={{ mb: 2 }}>
              <Typography
                color="#6D6D6D"
                variant="body2"
                component="div"
                sx={{ mb: 0.5 }}
              >
                Copy Number
              </Typography>
              <Chip
                label={cnv.copy_number || "N/A"}
                sx={{
                  height: 28,
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  backgroundColor:
                    cnv.copy_number < 2
                      ? "#d32f2f"
                      : cnv.copy_number === 2
                      ? "#757575"
                      : "#f57c00",
                  color: "white",
                  "& .MuiChip-label": {
                    px: 1.5,
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Exon and Quality Information Card */}
      <Grid item xs={12} md={6}>
        <Card sx={{ height: "100%", boxShadow: 2 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                pb: 1,
                borderBottom: "2px solid #2196f3",
                color: "#171A1E",
              }}
            >
              Structural Details
            </Typography>
            <DetailItem title="Affected Exons" value={cnv.exons} />
            <DetailItem
              title="Exon Range"
              value={
                cnv.start_exon && cnv.end_exon && cnv.total_exons
                  ? `${cnv.start_exon}-${cnv.end_exon} of ${cnv.total_exons} total exons`
                  : "N/A"
              }
            />
            <DetailItem
              title="Quality Score"
              value={
                cnv.quality_score ? `${cnv.quality_score.toFixed(1)}%` : "N/A"
              }
            />
            <DetailItem
              title="gnomAD SV Allele Frequency"
              value={
                cnv.gnomad_sv_af !== null && cnv.gnomad_sv_af !== undefined
                  ? parseFloat(cnv.gnomad_sv_af).toPrecision(4)
                  : "N/A"
              }
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Clinical Significance Card */}
      <Grid item xs={12}>
        <Card sx={{ boxShadow: 2 }}>
          <CardContent>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                pb: 1,
                borderBottom: "2px solid #2196f3",
                color: "#171A1E",
              }}
            >
              Clinical Interpretation
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box sx={{ mb: 2 }}>
                  <Typography
                    color="#6D6D6D"
                    variant="body2"
                    component="div"
                    sx={{ mb: 0.5 }}
                  >
                    Clinical Significance
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    component="div"
                    sx={{
                      color: getSignificanceColor(cnv.clinical_significance),
                      fontWeight: 600,
                    }}
                  >
                    {cnv.clinical_significance || "N/A"}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={8}>
                <DetailItem title="Description" value={cnv.description} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

export default CNVOverviewTable;
